import { useMemo } from 'react';
import { DataTable, MoreFiltersMenu } from '@odo/shared-ui';
import { buildMessageColumns } from './messageColumns';
import type { MessageTableRow } from './messageRows';
import {
    MessagesEmptyState,
    MessagesErrorState,
    MessagesSkeletonRows
} from './MessagesTableStates';
import { DM_INBOX_SEARCH_MAX_LENGTH } from '../../../services/dmNotifications';
import type { MessagesInbox } from './useMessagesInbox';

// Cell padding animates to zero alongside CollapsibleCell, so the whole row folds away.
const LEAVING_ROW_CLASS = '[&>td]:!py-0';

function rowClassName(row: MessageTableRow): string | undefined {
    return row.leaving ? LEAVING_ROW_CLASS : undefined;
}

interface MessagesTableProps {
    inbox: MessagesInbox;
    onOpenPatient: (patientId: string) => void;
}

export function MessagesTable({ inbox, onOpenPatient }: MessagesTableProps) {
    const { table, query, loadMore, search, filters, hasFilters, clearFilters } = inbox;
    const rows = query.isError ? [] : inbox.rows;
    const columns = useMemo(
        () => buildMessageColumns(rows.length, table.listSearch, table.status),
        [rows.length, table.listSearch, table.status]
    );
    const total = query.meta?.total ?? 0;
    const totalPages = Math.max(1, query.meta?.totalPages ?? 1);

    return (
        <DataTable
            label={table.status === 'closed' ? 'Resolved DM messages' : 'DM messages to review'}
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            rowAction={{
                onActivate: (row) => onOpenPatient(row.patient.patientId),
                mode: 'button',
                getLabel: (row) => `Open messages from ${row.patient.patientName}`
            }}
            rowClassName={rowClassName}
            rowHeightClassName=""
            sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
            mode={table.mode}
            onModeChange={table.setMode}
            {...loadMore}
            pagination={{ page: table.page, totalPages, total, onPage: table.setPage }}
            isLoading={query.isLoading}
            loadingRows={<MessagesSkeletonRows />}
            emptyState={
                query.isError ? (
                    <MessagesErrorState onRetry={() => void query.refresh()} />
                ) : (
                    <MessagesEmptyState
                        filtersActive={hasFilters}
                        onClearFilters={clearFilters}
                        closed={table.status === 'closed'}
                    />
                )
            }
            emptyStateCellClassName="px-3 py-16"
            search={{
                ...search,
                placeholder: 'Search patient or message',
                maxLength: DM_INBOX_SEARCH_MAX_LENGTH
            }}
            filters={filters}
            toolbar={
                <MoreFiltersMenu
                    categories={inbox.more}
                    allCategories={inbox.more}
                    hiddenActiveCount={inbox.moreActiveCount}
                />
            }
            className="overflow-hidden px-8 pb-8"
        />
    );
}
